/**
 * CreatePlan — Form to create a new trip plan from scratch.
 *
 * Route: /user/create-plan (inside UserLayout)
 *
 * Features:
 *  - Plan overview fields (title, location, duration, budget, currency, trip type, dates, description)
 *  - Initial days creation with title and notes
 *  - Validation before submission
 *  - Redirects to TripBuilder on success
 *  - Auth gating with return-to redirect
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  useTheme,
  Alert,
  Snackbar,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Stack,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  NavigateNext,
  NavigateBefore,
  CalendarMonth,
  LocationOn,
  Description,
} from '@mui/icons-material';
import plansService from '../../services/plansService';
import authService from '../../services/authService';
import ButtonPill from '../ui/ButtonPill';

const TRIP_TYPE_OPTIONS = [
  { value: 'safari', label: 'Safari', desc: 'Wildlife game drives and nature reserves' },
  { value: 'beach', label: 'Beach', desc: 'Coastal relaxation and water activities' },
  { value: 'cultural', label: 'Cultural', desc: 'Heritage sites and local experiences' },
  { value: 'adventure', label: 'Adventure', desc: 'Extreme sports and outdoor thrills' },
  { value: 'roadtrip', label: 'Road Trip', desc: 'Scenic drives and cross-country journeys' },
  { value: 'international', label: 'International', desc: 'Cross-border travel experiences' },
  { value: 'hiking', label: 'Hiking', desc: 'Mountain trails and trekking' },
  { value: 'camping', label: 'Camping', desc: 'Outdoor camping and wilderness stays' },
  { value: 'honeymoon', label: 'Honeymoon', desc: 'Romantic getaway packages' },
  { value: 'corporate', label: 'Corporate', desc: 'Team building and business retreats' },
];

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (\u20AC)' },
  { value: 'GBP', label: 'GBP (\u00A3)' },
  { value: 'KES', label: 'KES (KSh)' },
];

const steps = ['Plan Details', 'Trip Type & Budget', 'Initial Days'];

const CreatePlan = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeStep, setActiveStep] = useState(0);

  const [form, setForm] = useState({
    title: '',
    description: '',
    location: '',
    duration: '',
    total_budget: '',
    currency: 'USD',
    trip_type: 'safari',
    start_date: '',
    end_date: '',
    plan_days_attributes: [{ day: 1, title: '', notes: '', plan_items_attributes: [] }],
  });

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateDay = (index, field, value) => {
    setForm((prev) => {
      const days = [...prev.plan_days_attributes];
      days[index] = { ...days[index], [field]: value };
      return { ...prev, plan_days_attributes: days };
    });
  };

  const addDay = () => {
    const maxDay = form.plan_days_attributes.reduce((m, d) => Math.max(m, d.day || 0), 0);
    setForm((prev) => ({
      ...prev,
      plan_days_attributes: [
        ...prev.plan_days_attributes,
        { day: maxDay + 1, title: '', notes: '', plan_items_attributes: [] },
      ],
    }));
  };

  const removeDay = (index) => {
    if (form.plan_days_attributes.length <= 1) return;
    setForm((prev) => ({
      ...prev,
      plan_days_attributes: prev.plan_days_attributes.filter((_, i) => i !== index),
    }));
  };

  const canProceed = () => {
    if (activeStep === 0) return form.title?.trim().length > 0;
    if (activeStep === 1) return true;
    return true;
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!authService.isAuthenticated()) {
      navigate('/login', { state: { returnTo: '/user/create-plan' } });
      return;
    }
    if (!form.title?.trim()) {
      setSnackbar({ open: true, message: 'Please enter a plan title', severity: 'error' });
      setActiveStep(0);
      return;
    }
    submittingRef.current = true;
    try {
      setLoading(true);
      setError(null);
      const payload = {
        title: form.title.trim(),
        description: form.description?.trim() || null,
        location: form.location?.trim() || null,
        duration: form.duration?.trim() || null,
        total_budget: form.total_budget ? parseFloat(form.total_budget) : null,
        currency: form.currency,
        trip_type: form.trip_type,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        plan_days_attributes: form.plan_days_attributes.map((d, i) => ({
          day: d.day ?? i + 1,
          title: d.title?.trim() || null,
          notes: d.notes?.trim() || null,
          plan_items_attributes: d.plan_items_attributes || [],
        })),
      };
      const { plan, redirect_url } = await plansService.createPlan(payload);
      const url = redirect_url || (plan?.id ? `/builder/${plan.id}` : null) || '/user/plans';
      setSnackbar({
        open: true,
        message: 'Plan created! Redirecting to builder...',
        severity: 'success',
      });
      setTimeout(() => navigate(url), 500);
    } catch (err) {
      setError(err?.message ?? 'Failed to create plan');
      setSnackbar({
        open: true,
        message: err?.message ?? 'Failed to create plan',
        severity: 'error',
      });
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Plan Title"
                value={form.title}
                onChange={(e) => updateForm('title', e.target.value)}
                fullWidth
                required
                variant="outlined"
                placeholder="e.g. Kenya Safari Adventure"
                autoFocus
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Location"
                value={form.location}
                onChange={(e) => updateForm('location', e.target.value)}
                fullWidth
                variant="outlined"
                placeholder="e.g. Kenya, Tanzania"
                InputProps={{
                  startAdornment: (
                    <LocationOn sx={{ mr: 0.5, color: theme.palette.text.secondary, fontSize: 20 }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Duration"
                value={form.duration}
                onChange={(e) => updateForm('duration', e.target.value)}
                fullWidth
                variant="outlined"
                placeholder="e.g. 5 days"
                InputProps={{
                  startAdornment: (
                    <CalendarMonth sx={{ mr: 0.5, color: theme.palette.text.secondary, fontSize: 20 }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date"
                type="date"
                value={form.start_date}
                onChange={(e) => updateForm('start_date', e.target.value)}
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="End Date"
                type="date"
                value={form.end_date}
                onChange={(e) => updateForm('end_date', e.target.value)}
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => updateForm('description', e.target.value)}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                placeholder="Describe your trip plan..."
                InputProps={{
                  startAdornment: (
                    <Description sx={{ mr: 0.5, mt: 0.5, color: theme.palette.text.secondary, fontSize: 20, alignSelf: 'flex-start' }} />
                  ),
                }}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                What type of trip are you planning?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {TRIP_TYPE_OPTIONS.map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    onClick={() => updateForm('trip_type', opt.value)}
                    color={form.trip_type === opt.value ? 'primary' : 'default'}
                    variant={form.trip_type === opt.value ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 600,
                      px: 1,
                      transition: 'all 0.2s ease',
                      '&:hover': { transform: 'translateY(-2px)' },
                    }}
                  />
                ))}
              </Box>
              {form.trip_type && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {TRIP_TYPE_OPTIONS.find((o) => o.value === form.trip_type)?.desc}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Budget"
                type="number"
                value={form.total_budget}
                onChange={(e) => updateForm('total_budget', e.target.value)}
                fullWidth
                variant="outlined"
                placeholder="Optional — set your target budget"
                InputProps={{
                  startAdornment: (
                    <Typography sx={{ mr: 0.5, color: theme.palette.text.secondary, fontWeight: 600 }}>
                      {({ USD: '$', EUR: '\u20AC', GBP: '\u00A3', KES: 'KSh' }[form.currency]) || '$'}
                    </Typography>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={form.currency}
                  onChange={(e) => updateForm('currency', e.target.value)}
                  label="Currency"
                >
                  {CURRENCY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Initial Days
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add at least one day to get started. You can add more days, items, and details in the
              builder.
            </Typography>

            <Stack spacing={2}>
              {form.plan_days_attributes.map((day, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Chip
                      label={`Day ${day.day ?? index + 1}`}
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                    {form.plan_days_attributes.length > 1 && (
                      <ButtonPill
                        type="button"
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => removeDay(index)}
                      >
                        Remove
                      </ButtonPill>
                    )}
                  </Box>
                  <TextField
                    label="Day Title"
                    value={day.title}
                    onChange={(e) => updateDay(index, 'title', e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    placeholder="e.g. Arrival & Check-in"
                  />
                  <TextField
                    label="Notes"
                    value={day.notes}
                    onChange={(e) => updateDay(index, 'notes', e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Any initial notes for this day..."
                  />
                </Paper>
              ))}
            </Stack>

            <ButtonPill
              type="button"
              variant="outlined"
              startIcon={<Add />}
              onClick={addDay}
              sx={{ mt: 2 }}
            >
              Add Another Day
            </ButtonPill>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <ButtonPill
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/user/plans')}
          sx={{ mb: 3 }}
        >
          Back to My Plans
        </ButtonPill>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            border: `2px solid ${theme.palette.grey[200]}`,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: theme.palette.primary.main,
              fontFamily: '"Playfair Display", serif',
            }}
          >
            Create New Plan
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Build your trip from scratch. Fill in the basics, then customize everything in the
            builder.
          </Typography>

          {/* Stepper */}
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 4,
              '& .MuiStepIcon-root.Mui-active': { color: theme.palette.primary.main },
              '& .MuiStepIcon-root.Mui-completed': { color: theme.palette.primary.main },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Step content */}
            <Box sx={{ minHeight: 280 }}>{renderStepContent()}</Box>

            {/* Navigation */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <ButtonPill
                type="button"
                variant="outlined"
                onClick={activeStep === 0 ? () => navigate('/user/plans') : handleBack}
                startIcon={activeStep === 0 ? undefined : <NavigateBefore />}
                disabled={loading}
              >
                {activeStep === 0 ? 'Cancel' : 'Back'}
              </ButtonPill>

              {activeStep < steps.length - 1 ? (
                <ButtonPill
                  type="button"
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<NavigateNext />}
                  disabled={!canProceed()}
                >
                  Next
                </ButtonPill>
              ) : (
                <ButtonPill
                  type="submit"
                  variant="contained"
                  disabled={loading || !canProceed()}
                  startIcon={loading ? <CircularProgress size={18} /> : <Add />}
                >
                  {loading ? 'Creating...' : 'Create & Open Builder'}
                </ButtonPill>
              )}
            </Box>
          </form>
        </Paper>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreatePlan;
